/**
 * BucketsTab — one card per content bucket showing reference counts by
 * sub-category, bucket voice, and a "Refresh from Drive" trigger.
 * The scanner runs on a 6h pg_cron; this button is for on-demand re-scans
 * when someone drops new photos into the shared Drive folder.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FolderSync, ImageIcon, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { useContentBuckets, type BucketWithRefs } from '@/hooks/useContentBuckets';

export function BucketsTab() {
  const q = useContentBuckets();
  const qc = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState<string | null>(null);

  async function refresh() {
    setScanning(true);
    try {
      const r = await fetch('/api/content/scan', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const j = await r.json();
      if (j?.ok) {
        setLastScan(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));
        await qc.invalidateQueries({ queryKey: ['content-buckets'] });
      } else {
        setLastScan(`error: ${j?.error ?? 'unknown'}`);
      }
    } catch (e) {
      setLastScan(`error: ${(e as Error).message}`);
    } finally {
      setScanning(false);
    }
  }

  if (q.isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-40 w-full" />)}
      </div>
    );
  }
  if (q.isError) {
    return <EmptyState title="Couldn't load buckets" description={String(q.error)} />;
  }
  const buckets = q.data ?? [];
  if (buckets.length === 0) {
    return <EmptyState title="No buckets yet" description="Migration 0011 should have seeded 4 buckets — run it first." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {buckets.length} buckets · {buckets.reduce((s, b) => s + b.ref_count, 0)} references from Drive
          {lastScan && <span className="ml-2">· last scan {lastScan}</span>}
        </div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={scanning}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', scanning && 'animate-spin')} />
          {scanning ? 'Scanning…' : 'Rescan Drive'}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {buckets.map((b) => <BucketCard key={b.slug} bucket={b} />)}
      </div>
    </div>
  );
}

function BucketCard({ bucket }: { bucket: BucketWithRefs }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: bucket.color_accent ?? '#888' }}
            />
            {bucket.name}
            {bucket.is_suggested && (
              <span className="rounded bg-violet-500/10 px-1.5 py-0.5 text-2xs text-violet-500">
                suggested
              </span>
            )}
          </CardTitle>
          {bucket.description && (
            <p className="mt-1 text-xs text-muted-foreground">{bucket.description}</p>
          )}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div className="flex items-center gap-1 tabular-nums">
            <ImageIcon className="h-3 w-3" />
            {bucket.ref_count} refs
          </div>
          {bucket.target_monthly != null && (
            <div className="tabular-nums">target {bucket.target_monthly}/mo</div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-1">
        {bucket.sub_category_counts.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {bucket.sub_category_counts.map((sc) => (
              <span
                key={String(sc.sub_category ?? '_root')}
                className="rounded-full border border-border/50 bg-muted/30 px-2 py-0.5 text-2xs"
              >
                {sc.sub_category ?? '(loose)'} · {sc.count}
              </span>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-2xs text-muted-foreground/70">
            <FolderSync className="h-3 w-3" />
            No refs yet — add photos to /Flax Content Studio/References/{bucket.name}/…
          </div>
        )}
        {bucket.mood && (
          <div className="text-2xs text-muted-foreground">
            <span className="text-muted-foreground/60">mood: </span>
            {bucket.mood}
          </div>
        )}
        {bucket.brand_voice_notes && (
          <div className="text-2xs italic text-muted-foreground/80">
            "{bucket.brand_voice_notes}"
          </div>
        )}
      </CardContent>
    </Card>
  );
}
